import type { Entries } from "../../typings/utils"
import type { AnyCSVImporterColumns, ValueOfColumns } from "./typings"

interface CSVImporterConfig<TColumns extends AnyCSVImporterColumns> {
	columns: TColumns
	data: Record<string, string>[]
}

export class CSVImporter<TColumns extends AnyCSVImporterColumns> {
	private readonly columns: TColumns

	private readonly initialData: Record<string, string>[]
	private _validatedData: ValueOfColumns<TColumns>[] | null

	constructor(config: CSVImporterConfig<TColumns>) {
		const { columns, data } = config

		this.columns = columns
		this.initialData = data
		this._validatedData = null
	}

	validate() {
		// Transform process:

		const transformedDatas: ValueOfColumns<TColumns>[] = []

		for (const data of this.initialData) {
			const transformedData = this.transformData(this.columns, data)
			transformedDatas.push(transformedData)
		}

		// Validate process:

		const validatedDatas: ValueOfColumns<TColumns>[] = []

		for (const [dataIndex, data] of transformedDatas.entries()) {
			validatedDatas.push(this.validateData(this.columns, dataIndex, data))
		}

		this._validatedData = validatedDatas
	}

	get validatedData() {
		const validatedData = this._validatedData

		if (!validatedData) {
			throw new Error("Attempting to get validated data before validate() has been called.")
		}

		return validatedData
	}

	private transformData = <TColumns extends AnyCSVImporterColumns>(
		columns: TColumns,
		data: Record<string, string>,
	) => {
		// @ts-expect-error REASON: Generic typing causing typescript to lie
		const transformedData: ValueOfColumns<TColumns> = {}

		for (const [columnKey, column] of Object.entries(columns) as Entries<TColumns>) {
			if ("columns" in column) {
				// @ts-expect-error REASON: Generic typing causing typescript to lie
				transformedData[columnKey] = this.transformData(column.columns, data)
			} else {
				if (!(column.header in data)) continue
				transformedData[columnKey] = column.transformValue(data[column.header])
			}
		}

		return transformedData
	}

	private validateData = <TColumns extends AnyCSVImporterColumns>(
		columns: TColumns,
		dataIndex: number,
		data: ValueOfColumns<TColumns>,
	) => {
		// @ts-expect-error REASON: Generic typing causing typescript to lie
		const validatedData: ValueOfColumns<TColumns> = {}

		for (const [columnKey, column] of Object.entries(columns) as Entries<TColumns>) {
			if ("columns" in column) {
				// @ts-expect-error REASON: Generic typing causing typescript to lie
				validatedData[columnKey] = this.validateData(column.columns, dataIndex, data[columnKey])
			} else {
				if (!(columnKey in data) && column.required) {
					throw new Error(`The column ${column.header} is required.`)
				}

				if (columnKey in data) {
					if (column.validateValue && !column.validateValue(data[columnKey])) {
						throw new Error(
							`The value provided for column ${column.header} at row ${dataIndex + 1} is invalid.`,
						)
					}

					validatedData[columnKey] = data[columnKey]
				}
			}
		}

		return validatedData
	}
}
